import { useNavigate } from "@tanstack/react-router";
import { toast } from "@flint/ui";
import { StoreAdminLayout } from "../store-admin-layout.tsx";
import { BackButton } from "../../workspace/index.ts";
import { StorePageColumn } from "../store-page-column.tsx";
import { StoreFormSection } from "../products/store-form-section.tsx";
import { trpc } from "../../../lib/trpc.ts";
import { storeErrorMessage } from "../error-message.ts";
import {
  CollectionForm,
  EMPTY_COLLECTION,
  toCollectionPayload,
  type CollectionFormValues,
} from "./collection-form.tsx";

/**
 * Store admin — New collection (Commerce, M6).
 *
 * Products are curated on the detail page, matching the wireframe's own help
 * text ("Add now or curate later from the collection detail page"). Rather
 * than build a picker that writes nothing until the collection exists, this
 * screen creates first and lands the operator on Detail, where the real picker
 * lives.
 *
 * @see prototype/store/admin/all.html#collections-new
 */
export function CollectionNewPage() {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const create = trpc.storeCollections.create.useMutation();

  async function onSubmit(v: CollectionFormValues) {
    try {
      const res = await create.mutateAsync(toCollectionPayload(v));
      await utils.storeCollections.list.invalidate();
      toast.success("Collection created", { description: "Add products to it next." });
      await navigate({
        to: "/store/admin/collections/$collectionId",
        params: { collectionId: res.collectionId },
      });
    } catch (err) {
      toast.error("Couldn't create collection", { description: storeErrorMessage(err) });
    }
  }

  return (
    <StoreAdminLayout
      breadcrumbs={[
        { label: "Store", to: "/store/admin" },
        { label: "Collections", to: "/store/admin/collections" },
        { label: "New collection" },
      ]}
    >
      <StorePageColumn>
        <div className="flex items-center gap-3">
          <BackButton fallbackTo="/store/admin/collections" label="Back to collections" />
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-ink leading-tight">
              New collection
            </h1>
            <p className="mt-1.5 text-sm text-muted">
              Build a line sheet — your always-orderable Core catalog, or a seasonal drop.
            </p>
          </div>
        </div>

        <CollectionForm
          defaultValues={EMPTY_COLLECTION}
          busy={create.isPending}
          submitLabel="Create collection"
          onSubmit={onSubmit}
          onCancel={() => void navigate({ to: "/store/admin/collections" })}
        >
          <StoreFormSection
            title="Products"
            help="Add now or curate later from the collection detail page."
          >
            <p className="text-sm text-muted">
              Create the collection first — you'll pick its products on the next screen.
            </p>
          </StoreFormSection>
        </CollectionForm>
      </StorePageColumn>
    </StoreAdminLayout>
  );
}
